import { TUNABLES } from "../config";
import { jsonRecord, MAX_SESSION_CHARS, oneOf, text } from "./protocol";

/**
 * NM2, names owned by accounts on the wire: what the page posts to the Worker's `/names` and what
 * it answers. Shared by the page (`src/front/nameClaim.ts`), the Worker
 * (`server/cloudflare/namesEndpoint.ts`) and the room's check of a join (`roomObject.ts`), so all
 * read one shape and one key of a name.
 *
 * A signed-in account claims its name with its session (`server/session.ts`): the Worker keeps the
 * name's key with the account's code, one name an account, and a claim of another name lets the
 * first go. Anyone may ask whether a name is free; nobody but its holder sits in a room under it.
 * The name is a label: what a player earns is keyed by the account, never by the name.
 */

export type NamesRequest =
  | { readonly kind: "claim"; readonly session: string; readonly name: string }
  | { readonly kind: "check"; readonly name: string };

/**
 * Why a claim was turned down: another account holds the name; the name has the shape a room
 * gives a stand-in; the session is not one the Worker made or has run out; the request is not
 * one; the Worker keeps no names (its switch is off); the Worker lacks its key.
 */
export const NAME_REFUSALS = ["taken", "reserved", "session", "shape", "off", "setup"] as const;
export type NameRefusal = (typeof NAME_REFUSALS)[number];

export type NamesAnswer =
  | { readonly held: string }
  | { readonly free: boolean }
  | { readonly refused: NameRefusal };

/** A name as the page cleans it (`cleanName`): words, one space between them, none around. */
const CLEAN_NAME = /^\S+( \S+)*$/u;

/** A request from a page, checked; undefined when it is not one. */
export function parseNamesRequest(raw: string): NamesRequest | undefined {
  if (raw.length > TUNABLES.names.maxRequestChars) {
    return undefined;
  }
  const value = jsonRecord(raw);
  const name = text(value?.name, TUNABLES.landing.nameMaxLength);
  if (!name || !CLEAN_NAME.test(name)) {
    return undefined;
  }
  if (value?.kind === "check") {
    return { kind: "check", name };
  }
  const session = text(value?.session, MAX_SESSION_CHARS);
  return value?.kind === "claim" && session ? { kind: "claim", session, name } : undefined;
}

/** The Worker's answer, checked; undefined for anything else (an older Worker, a proxy's page). */
export function parseNamesAnswer(raw: string): NamesAnswer | undefined {
  const value = jsonRecord(raw);
  const held = text(value?.held, TUNABLES.landing.nameMaxLength);
  if (held) {
    return { held };
  }
  if (typeof value?.free === "boolean") {
    return { free: value.free };
  }
  const refused = oneOf(NAME_REFUSALS, value?.refused);
  return refused ? { refused } : undefined;
}

/** The names endpoint beside a lobby server: `wss://host` becomes `https://host/names`. */
export function namesUrlOf(lobbyUrl: string): string {
  return `${lobbyUrl.replace(/^ws(s?):\/\//, "http$1://")}/names`;
}

/**
 * Letters of other scripts drawn like Latin ones, folded into them for a name's key: "Grumpy
 * Badger" typed with a Cyrillic "а" or a Greek "ο" is the same name.
 */
const LOOK_ALIKES: Readonly<Record<string, string>> = {
  а: "a",
  в: "b",
  е: "e",
  ё: "e",
  і: "i",
  ї: "i",
  ј: "j",
  к: "k",
  м: "m",
  н: "h",
  о: "o",
  р: "p",
  с: "c",
  т: "t",
  у: "y",
  х: "x",
  ѕ: "s",
  ԁ: "d",
  ɡ: "g",
  ӏ: "l",
  α: "a",
  β: "b",
  ε: "e",
  ι: "i",
  κ: "k",
  ν: "v",
  ο: "o",
  ρ: "p",
  τ: "t",
  υ: "u",
  χ: "x",
};

/**
 * The key two names share when a player would read them as one: Unicode's compatibility form,
 * lower case, no accents, look-alike letters folded, nothing but letters and digits. "Tejón
 * Gruñón", "TEJON  GRUNON" and "tejon-grunon" have one key; a claim holds the key.
 */
export function nameKeyOf(name: string): string {
  const plain = name.normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase().normalize("NFKC");
  return [...plain]
    .map((char) => LOOK_ALIKES[char] ?? char)
    .filter((char) => /[\p{L}\p{N}]/u.test(char))
    .join("");
}

/** The word of a room's stand-in name; a name of its shape is never claimed. */
const STAND_IN_WORD = "Guest";
const STAND_IN = /^guest\d{3}$/;
/** A stand-in as a room wrote it, or the bare word a page that kept no name sits with (`src/boot.ts`). */
const STAND_IN_SHOWN = /^Guest(?: (\d{3}))?$/;

/** The word a page sits with when it kept no name (`src/boot.ts`'s rejoin). */
export const GUEST_WORD = STAND_IN_WORD;

/**
 * L10N3: a name as a screen shows it: a room's stand-in ("Guest 417", or the bare word) in the
 * reader's language through `guest` (its digits, or "" for the bare word), any other as typed.
 */
export function shownName(name: string, guest: (number: string) => string): string {
  const match = STAND_IN_SHOWN.exec(name);
  return match ? guest(match[1] ?? "") : name;
}

/** A name of the stand-in's shape ("Guest 417"): no account may claim it. */
export function isReservedName(name: string): boolean {
  return STAND_IN.test(nameKeyOf(name));
}

/**
 * The name a room gives one who came under a name another account holds: "Guest" and three
 * digits from the player's ID, the same at every rejoin.
 */
export function standInName(playerId: string): string {
  let hash = 2166136261;
  for (const char of playerId) {
    hash = Math.imul(hash ^ (char.codePointAt(0) ?? 0), 16777619) >>> 0;
  }
  return `${STAND_IN_WORD} ${100 + (hash % 900)}`;
}
